import { AlertCircle, ChevronDown, ChevronUp, CircleSlash, FileText, FolderOpen, X } from "lucide-react";
import { useState } from "react";
import { MAX_FILES_PER_UPLOAD, UPLOAD_ACCEPT_STRING, UPLOAD_EXTENSIONS_LABEL } from "@/constants/files";
import { useFileDropzone } from "@/hooks/useFileDropzone";
import { mergeUploadFiles, partitionUploadFiles } from "@/lib/files";

const AUTO_EXPAND_FILE_LIMIT = 5;

export interface FilePickerProps {
  selectedFiles: File[];
  rejectedFiles: string[];
  onChange: (accepted: File[], rejected: string[]) => void;
  inputId?: string;
}

function formatMegabytes(bytes: number): string {
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export function FilePicker({ selectedFiles, rejectedFiles, onChange, inputId }: Readonly<FilePickerProps>) {
  const [showFileList, setShowFileList] = useState(false);
  const [overflowCount, setOverflowCount] = useState(0);

  const processFiles = (allFiles: File[]) => {
    const { accepted, rejected } = partitionUploadFiles(allFiles);
    const { files, overflow } = mergeUploadFiles(selectedFiles, accepted, MAX_FILES_PER_UPLOAD);
    setOverflowCount(overflow);
    setShowFileList(files.length <= AUTO_EXPAND_FILE_LIMIT);
    onChange(files, rejected);
  };

  const handleRemoveFile = (index: number) => {
    setOverflowCount(0);
    onChange(
      selectedFiles.filter((_, idx) => idx !== index),
      rejectedFiles,
    );
  };

  const { isDragging, handleDragOver, handleDragLeave, handleDrop } = useFileDropzone(processFiles);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    processFiles(Array.from(e.target.files));
    e.target.value = "";
  };

  const totalSize = selectedFiles.reduce((sum, file) => sum + file.size, 0);
  const fileCountLabel = `${selectedFiles.length} file${selectedFiles.length === 1 ? "" : "s"} selected`;

  return (
    <div className="space-y-4">
      <label
        onDragEnter={handleDragOver}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`block w-full border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer has-[input:focus-visible]:ring-2 has-[input:focus-visible]:ring-brand-primary/50 ${
          isDragging ? "border-brand-primary bg-brand-primary/5" : "border-border-secondary hover:border-border-hover"
        }`}
      >
        <FolderOpen className="pointer-events-none h-10 w-10 text-content-quaternary mx-auto mb-3" />
        <span className="pointer-events-none block text-sm text-content-primary font-medium">
          Drop files here or click to browse
        </span>
        <span className="pointer-events-none block text-xs text-content-quaternary mt-1">
          {UPLOAD_EXTENSIONS_LABEL} up to 100MB each
        </span>
        <span className="pointer-events-none block text-xs text-content-quaternary mt-1">
          Up to {MAX_FILES_PER_UPLOAD} files per upload
        </span>
        <input
          id={inputId}
          type="file"
          multiple
          accept={UPLOAD_ACCEPT_STRING}
          onChange={handleFileSelect}
          className="sr-only"
        />
      </label>
      {selectedFiles.length > 0 && (
        <div className="rounded-lg border border-border-primary">
          <button
            type="button"
            onClick={() => setShowFileList((prev) => !prev)}
            aria-expanded={showFileList}
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-content-secondary hover:text-content-primary transition-colors"
          >
            <FileText className="h-4 w-4 text-content-quaternary" />
            <span className="flex-1 text-left font-medium">{fileCountLabel}</span>
            <span className="text-content-quaternary">{formatMegabytes(totalSize)}</span>
            {showFileList ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {showFileList && (
            <div className="max-h-60 overflow-y-auto space-y-1 border-t border-border-primary p-2">
              {selectedFiles.map((file, idx) => (
                <div
                  key={`file-${file.name}-${idx}`}
                  className="flex items-center gap-2 text-sm text-content-secondary bg-surface-secondary px-3 py-2 rounded-sm"
                >
                  <FileText className="h-4 w-4 shrink-0 text-content-quaternary" />
                  <span className="flex-1 truncate">{file.name}</span>
                  <span className="shrink-0 text-content-quaternary">{formatMegabytes(file.size)}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveFile(idx)}
                    aria-label={`Remove ${file.name}`}
                    className="shrink-0 p-0.5 rounded-sm text-content-quaternary hover:text-content-primary transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {overflowCount > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <p className="text-sm text-amber-400 font-medium flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4 shrink-0" />
            Maximum {MAX_FILES_PER_UPLOAD} files per upload. {overflowCount} file
            {overflowCount === 1 ? " was" : "s were"} not added.
          </p>
        </div>
      )}
      {rejectedFiles.length > 0 && (
        <div className="space-y-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
          <p className="text-sm text-red-400 font-medium flex items-center gap-1.5">
            <AlertCircle className="h-4 w-4" />
            {rejectedFiles.length} file{rejectedFiles.length === 1 ? "" : "s"} not supported:
          </p>
          <div className="max-h-32 overflow-y-auto space-y-2">
            {rejectedFiles.map((name, idx) => (
              <div key={`rejected-${name}-${idx}`} className="flex items-center gap-2 text-sm text-red-300/80 px-2">
                <CircleSlash className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{name}</span>
              </div>
            ))}
          </div>
          <p className="text-xs text-content-quaternary px-2">Supported: {UPLOAD_EXTENSIONS_LABEL}.</p>
        </div>
      )}
    </div>
  );
}
